import { FormikProvider, useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'

import Button from 'components/common/Button'
import Flex from 'components/common/Flex'
import FormField from 'components/common/FormField'
import FormFieldTextArea from 'components/common/FormFieldTextArea'
import FormRow from 'components/common/FormRow'
import Panel from 'components/common/Panel'
import Text from 'components/common/Text'
import Modal, {
  ModalCloseButton,
  ModalHeader,
  ModalTitle
} from 'components/Modal'
import theme from 'theme'
import { track } from 'track'

function CreateScenarioModal({ isOpen, setIsOpen, onSubmitForm, ...rest }) {
  const form = useFormik({
    initialValues: {
      name: '',
      hypothesis: '',
      description: ''
    },
    validateOnChange: true,
    validateOnMount: true,
    validate: (fields) => {
      if (String(fields.name).trim().length < 1) {
        return {
          name: 'Please enter a name'
        }
      }
      return true
    }
  })

  const handleFormSubmit = () => {
    setIsOpen(false)
    onSubmitForm(
      form.values.name,
      form.values.description,
      form.values.hypothesis
    )
  }

  return (
    <Modal css={{ width: '32em' }} isOpen={isOpen} {...rest}>
      <ModalHeader>
        <ModalCloseButton onClick={() => setIsOpen(false)} />
        <ModalTitle>Create Scenario</ModalTitle>
      </ModalHeader>
      <Flex data-test-create-scenario-modal column={true}>
        <FormikProvider value={form}>
          <FormRow>
            <FormField
              data-test-scenario-name
              required
              css={{
                marginBottom: 0
              }}
              label="Name"
              name="name"
            />
          </FormRow>
          <FormRow>
            <FormFieldTextArea
              data-test-scenario-description
              required
              label="Description"
              name="description"
              subtext="What does this Scenario test?"
            />
          </FormRow>
          <FormRow>
            <Flex css={{ justifyContent: 'space-between' }}>
              <Button secondary onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                data-test-create-scenario-button
                data-track-action="create-scenario-from-attack-details"
                data-track-category="scenario"
                data-track-on="click"
                disabled={!form.isValid}
                onClick={() => {
                  track('CreateScenarioFromAttack')
                  handleFormSubmit()
                }}
              >
                Create Scenario
              </Button>
            </Flex>
          </FormRow>
        </FormikProvider>
      </Flex>
    </Modal>
  )
}

CreateScenarioModal.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  onSubmitForm: PropTypes.func
}

function CreateScenarioFromAttack({
  rerunLink,
  isShowCreateScenario,
  onSubmit,
  ...rest
}) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const history = useHistory()

  return (
    <Panel data-test-create-scenario-from-attack-header {...rest}>
      <div
        css={{
          display: 'grid',
          gap: theme.space[3],

          [theme.mq[1]]: {
            gridTemplateColumns: '1fr auto'
          }
        }}
      >
        <div>
          <Text as="h4" css={{ color: theme.colors.steel[2] }}>
            Create a Scenario to automate rerunning your attacks
          </Text>
          <Text>
            After each run of a Scenario, you can also record your observations.
          </Text>
        </div>
        <div css={{ whiteSpace: 'nowrap' }}>
          {isShowCreateScenario && (
            <Button
              data-test-open-create-scenario-modal
              data-track-action="create-scenario-from-attack-details-modal"
              data-track-category="scenario"
              data-track-on="click"
              onClick={() => {
                track('CreateScenarioFromAttackModal')
                setIsCreateModalOpen(true)
              }}
            >
              Create Scenario
            </Button>
          )}
          <Button
            data-test-rerun-attack
            secondary
            css={{
              marginLeft: theme.space[3]
            }}
            data-track-action="rerun-attack-from-create-scenario-header"
            data-track-category="attack"
            data-track-on="click"
            onClick={() => {
              history.push(rerunLink)
              track('RerunAttackFromCreateScenarioHeader')
            }}
          >
            Rerun
          </Button>
          <CreateScenarioModal
            isOpen={isCreateModalOpen}
            setIsOpen={setIsCreateModalOpen}
            onSubmitForm={onSubmit}
          />
        </div>
      </div>
    </Panel>
  )
}

CreateScenarioFromAttack.propTypes = {
  rerunLink: PropTypes.object,
  isShowCreateScenario: PropTypes.bool,
  onSubmit: PropTypes.func
}

export default CreateScenarioFromAttack
